/**
 * Loads every model and texture set of the manifest at boot (plan G6). Loads run in parallel; progress is
 * reported per finished asset; the first failure rejects with an `AssetLoadError` naming the asset id.
 * An optional `include` predicate limits the load to some asset ids (M2: boot skips the deferred station and prop
 * models, which the career feature loads after the first frame); progress then counts only included assets.
 */
import type { AssetsFile } from '../data/assetSchema'
import type { AssetLoader, LoadedModel, LoadedTextureSet } from './loader'

export type AssetSource = Pick<AssetLoader, 'loadModel' | 'loadTextureSet'>

export interface LoadedAssets {
  readonly models: ReadonlyMap<string, LoadedModel>
  readonly textureSets: ReadonlyMap<string, LoadedTextureSet>
}

export class AssetLoadError extends Error {
  readonly assetId: string

  constructor(assetId: string, cause: unknown) {
    super(`Asset ${assetId} failed to load: ${cause instanceof Error ? cause.message : String(cause)}`, { cause })
    this.name = 'AssetLoadError'
    this.assetId = assetId
  }
}

export type AssetProgress = (done: number, total: number, id: string) => void

/** Which asset ids to load (true = load). */
export type AssetFilter = (id: string) => boolean

/** How many models and texture sets of `assets` pass `include` (all without one). */
export function countAssets(assets: AssetsFile, include?: AssetFilter): number {
  if (!include) return assets.models.length + assets.textureSets.length
  let n = 0
  for (const m of assets.models) if (include(m.id)) n++
  for (const t of assets.textureSets) if (include(t.id)) n++
  return n
}

export async function loadAllAssets(
  source: AssetSource,
  assets: AssetsFile,
  onProgress?: AssetProgress,
  include?: AssetFilter,
): Promise<LoadedAssets> {
  const models = new Map<string, LoadedModel>()
  const textureSets = new Map<string, LoadedTextureSet>()
  const modelEntries = include ? assets.models.filter((e) => include(e.id)) : assets.models
  const textureEntries = include ? assets.textureSets.filter((e) => include(e.id)) : assets.textureSets
  const total = modelEntries.length + textureEntries.length
  let done = 0
  const finish = (id: string): void => {
    done++
    onProgress?.(done, total, id)
  }
  const guard = async <T>(id: string, load: () => Promise<T>): Promise<T> => {
    try {
      return await load()
    } catch (err: unknown) {
      throw new AssetLoadError(id, err)
    }
  }
  await Promise.all([
    ...modelEntries.map(async (entry) => {
      models.set(entry.id, await guard(entry.id, () => source.loadModel(entry)))
      finish(entry.id)
    }),
    ...textureEntries.map(async (entry) => {
      textureSets.set(entry.id, await guard(entry.id, () => source.loadTextureSet(entry)))
      finish(entry.id)
    }),
  ])
  return { models, textureSets }
}
